// components/imports/MappingGrid.tsx — wizard step 4 (Task O): each field of the kind against the file's columns, with
// sample values, how it was matched (Alto preset / by name / by hand) and the company + cut-over date. Saving a change
// sends the job back to "Uploaded": the dry run must be done again.
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Card, Badge, Field, Input, Select } from '../ui';
import { FormError } from '../forms/common';
import { importsApi, type JobDetail } from '../../lib/imports';

export function MappingGrid({ job, readOnly }: { job: JobDetail; readOnly?: boolean }) {
  const qc = useQueryClient();
  const companies = useQuery({ queryKey: ['import-companies'], queryFn: importsApi.companies });
  const save = useMutation({
    mutationFn: (b: Parameters<typeof importsApi.mapping>[1]) => importsApi.mapping(job.id, b),
    onSuccess: (j) => { qc.setQueryData(['import', job.id], j); void qc.invalidateQueries({ queryKey: ['import-report', job.id] }); },
  });
  const cols = job.mapping.columns ?? {};
  const used = new Set(Object.values(cols));
  const mapped = job.fields.filter((f) => cols[f.key]).length;
  const missing = job.fields.filter((f) => f.required && !cols[f.key]);

  return (
    <Card title="Columns" subtitle={`${mapped} of ${job.fields.length} fields matched · ${job.rowCount} rows · ${job.headers.length} columns in the file`}
      actions={job.presets.find((p) => p.key === job.preset)?.toConfirm ? <Badge value="Alto preset — to confirm" tone="warning" /> : undefined}>
      <div className="-m-4"><div className="grid grid-cols-1 gap-3 border-b border-ink-100 px-4 py-3 sm:grid-cols-2 sm:px-5 [&>*]:min-w-0">
        <Field label="Preset">
          <Select value={job.preset ?? ''} disabled={readOnly || save.isPending} onChange={(e) => save.mutate({ preset: e.target.value })}>
            {job.presets.map((p) => <option key={p.key} value={p.key}>{p.label}</option>)}
          </Select>
        </Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Company">
            <Select value={job.entityId ?? ''} disabled={readOnly || save.isPending} onChange={(e) => save.mutate({ entityId: e.target.value || null })}>
              <option value="">—</option>
              {(companies.data ?? []).map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </Select>
          </Field>
          <Field label="As at">
            <Input type="date" value={job.asOf ?? ''} disabled={readOnly || save.isPending} onChange={(e) => save.mutate({ asOf: e.target.value || null })} />
          </Field>
        </div>
      </div>
      {missing.length > 0 && <p className="border-b border-ink-100 bg-amber-50 px-5 py-2 text-[12px] text-amber-800">Still to map: {missing.map((f) => f.label).join(', ')}</p>}
      <FormError error={save.error} />
      <div className="divide-y divide-ink-100">
        {job.fields.map((f) => {
          const h = cols[f.key];
          const by = job.mapping.matchedBy?.[f.key];
          return (
            <div key={f.key} className="grid gap-2 px-4 py-2.5 sm:grid-cols-[minmax(0,1fr)_minmax(0,1.1fr)_minmax(0,1.2fr)] sm:items-center sm:px-5">
              <div className="min-w-0">
                <span className="text-[13px] font-medium text-ink-900">{f.label}{f.required && <span className="text-red-600"> *</span>}</span>
                {f.hint && <span className="block text-[11px] text-ink-400">{f.hint}</span>}
              </div>
              <Select aria-label={`Column for ${f.label}`} value={h ?? ''} disabled={readOnly || save.isPending} onChange={(e) => save.mutate({ columns: { [f.key]: e.target.value || null } })}>
                <option value="">— not in the file —</option>
                {job.headers.map((x) => <option key={x} value={x} disabled={used.has(x) && x !== h}>{x}</option>)}
              </Select>
              <div className="flex min-w-0 items-center gap-2">
                {h && by && <Badge value={by === 'preset' ? 'Preset' : 'By name'} tone={by === 'preset' ? 'good' : 'neutral'} />}
                <span className="truncate text-[11.5px] text-ink-500" title={(h ? job.samples[h] : [])?.join(' · ')}>{h ? (job.samples[h] ?? []).join(' · ') || 'empty' : ''}</span>
              </div>
            </div>
          );
        })}
      </div>
      {job.headers.filter((x) => !used.has(x)).length > 0 && (
        <p className="border-t border-ink-100 px-5 py-2.5 text-[11.5px] text-ink-400">Not brought over: {job.headers.filter((x) => !used.has(x)).join(', ')}</p>
      )}
    </div></Card>
  );
}
